import React from 'react'
import { IonItem, IonLabel, IonList, IonText } from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { TFunction, useTranslation } from 'react-i18next'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { resetQaAddendumDraftInputs, setSelectedFlagId } from '../qaAddendums/store/qaAddendum.actions'
import { isFlagListPRIDBlackedOut, LockType } from '../utils/qaFlagBlackout.util'
import { canViewFlagSelector } from '../../user/selectors'
import { getNotificationCenterSelector } from '../../notificationCenter/selectors'
import { markNotificationRead, setOpenedFlagId } from '../../notificationCenter/store/notificationCenter.actions'
import {
  INotificationDisplayItem,
  INotificationListItem,
} from '../../notificationCenter/store/notificationCenter.types'
import { formatDateTime } from '../../../utilities/dateTime.util'
import './QaFlags.component.css'

interface IQaFlagsProps {
  onCloseMenu: () => void
}

const getNotificationIcon = (item: INotificationDisplayItem): React.ReactNode => {
  if (!item.isRead) return <span className='qa-flags-item-dot' />
  return null
}

const buildFlagTitle = (item: INotificationListItem, t: TFunction<'translation'>): React.ReactNode => (
  <>
    <IonText className='qa-flags-item-emphasis fw-700'>{t('qaFlags.flagAddedId', { flagId: item.id })}</IonText>
    <IonText className='fw-400'>{t('qaFlags.flagAddedAction')}</IonText>
  </>
)

const buildFlagBody = (item: INotificationListItem, t: TFunction<'translation'>, canViewFlag: boolean): string => {
  const isBlackedOut = isFlagListPRIDBlackedOut(item.lockType ?? LockType.Normal, canViewFlag)
  const bodyKey = `qaFlags.flagAddedChartLine${isBlackedOut ? 'BlackedOut' : ''}`

  return isBlackedOut ? t(bodyKey) : t(bodyKey, { prid: item.prid })
}

const QaFlagRow: React.FC<{ item: INotificationDisplayItem }> = ({ item }) => (
  <IonItem button detail={false} className='qa-flags-item' onClick={item.onPress} data-testid='NotificationCenterItem'>
    <div slot='start' className='qa-flags-item-icon'>
      {getNotificationIcon(item)}
    </div>
    <IonLabel className='qa-flags-item-label'>
      <IonText className='qa-flags-item-title'>{item.title}</IonText>
      {item.body && <IonText className='qa-flags-item-body fw-400 m-0'>{item.body}</IonText>}
      <IonText className='qa-flags-item-timestamp fw-400 m-0'>{formatDateTime(item.timestamp)}</IonText>
    </IonLabel>
  </IonItem>
)

const QaFlagsEmptyState: React.FC<{ text: string }> = ({ text }) => (
  <div className='qa-flags-empty ion-padding' data-testid='NotificationCenterEmptyState'>
    <IonText color='medium'>{text}</IonText>
  </div>
)

const QaFlags: React.FC<IQaFlagsProps> = ({ onCloseMenu }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { items } = useSelector(getNotificationCenterSelector)
  const canViewFlag = useSelector(canViewFlagSelector)

  const handleNotificationPress = (item: INotificationListItem) => {
    if (item.flagType !== 'A') return

    dispatch(setOpenedFlagId(item.id))
    dispatch(markNotificationRead(item.id))
    dispatch(resetQaAddendumDraftInputs())
    dispatch(setSelectedFlagId(String(item.id)))
    onCloseMenu()
    history.push(AppRouterConstants.QA_ADDENDUM_FLAG_RESOLUTION_ROUTE, { from: history.location.pathname })
  }

  const displayItems: INotificationDisplayItem[] = items.map((item) => ({
    id: String(item.id),
    timestamp: item.createdAt,
    isRead: item.isRead,
    title: buildFlagTitle(item, t),
    body: <IonText>{buildFlagBody(item, t, canViewFlag)}</IonText>,
    onPress: () => handleNotificationPress(item),
  }))

  if (displayItems.length === 0) {
    return <QaFlagsEmptyState text={t('qaFlags.emptyState')} />
  }

  return (
    <IonList lines='full'>
      {displayItems.map((item) => (
        <QaFlagRow key={item.id} item={item} />
      ))}
    </IonList>
  )
}

export default QaFlags
